/**
 * Analytics Events Constants
 *
 * This file defines all analytics event names used throughout the application.
 * Organizing events into categories makes them easier to manage and ensures consistency.
 */
export const ANALYTIC_EVENTS = {
  // Join flow events
  // Add to Cart
  ADD_TO_CART: "Join.AddToCart",
  // Address Check
  ADDRESS_ENTERED: "Join.AddressEntered",
  // Waitlist
  WAITLIST_JOINED: "Join.WaitlistJoined",
  // Complete Registration
  ACCOUNT_REGISTRATION: "Join.AccountRegistration",
  // Email Verified
  EMAIL_VERIFIED: "Join.EmailVerified",
  // Initiate Checkout
  INITIATE_CHECKOUT: "Join.InitiateCheckout",
  // Payment Method Added
  PAYMENT_METHOD_ADDED: "Join.PaymentMethodAdded",
  // Payment Method Removed
  PAYMENT_METHOD_REMOVED: "Join.PaymentMethodRemoved",
  // Payment Method Add Failed
  PAYMENT_METHOD_ADD_FAILED: "Join.PaymentMethodAddFailed",
  // Payment Method Changed
  PAYMENT_METHOD_CHANGED: "Join.PaymentMethodChanged",
  // Purchase
  PURCHASE: "Join.Purchase",
  // Subscribe
  SUBSCRIBE: "Join.Subscribe",
  // Mobile Registration
  MOBILE_REGISTRATION: "Join.MobileRegistration",
  // Mobile Verified
  MOBILE_VERIFIED: "Join.MobileVerified",
  // Mobile App Download
  MOBILE_APP_DOWNLOAD: "Join.MobileAppDownload",
  // Sign Out
  SIGN_OUT: "Join.SignOut",
  // Sign In
  SESSION_EXPIRED: "Join.SessionExpired",
  // Help / Support
  HELP_CHAT_OPENED: "Help.ChatOpened",
  HELP_CALLED: "Help.Called",
  // Order
  ORDER_CREATED: "Order.Created",
  ORDER_ORDER_CREATED_FAILED: "Order.Created.Failed",
  ORDER_DETAIL_VIEWED: "Order.DetailViewed",
  ORDER_MESSAGE_SENT: "Order.MessageSent",

  TIME_PROPOSAL_ACCEPTED: "TimeProposal.Accepted",
  TIME_PROPOSAL_DECLINED: "TimeProposal.Declined",

  ACTIVITY_PAGE_VIEWED: "Activity.PageViewed",

  PROFILE_AVATAR_UPDATED: "Profile.AvatarUpdated",
  PROFILE_AVATAR_REMOVED: "Profile.AvatarRemoved",
  PROFILE_UPDATED: "Profile.Updated",

  ONBOARDING_PROGRESS: "Onboarding.Progress",

  INVITE_ACCEPTED: "Invite.Accepted",
  INVITE_JOIN: "Invite.Join",
  INVITE_VERIFY: "Invite.Verify",
  INVITE_SUCCESS: "Invite.Success",
} as const;

/**
 * Founding-member presale flow (`/presale/*`).
 *
 * A PARALLEL set to the `Join.*` events above, not a reuse of them: the two funnels sell
 * different carts and must be countable apart. The PAYLOAD keys are deliberately identical
 * to Join's (see `features/presale/_libs/presale-analytics-props.ts`), so one Mixpanel
 * query serves both funnels and only the event name tells them apart.
 *
 * Only the twelve steps this funnel actually has: no `Mobile*` (there is no phone step),
 * no `PaymentMethodRemoved` (nothing here deletes a card), no `SessionExpired`
 * (`AUTH_EVENTS` already owns that).
 */
export const PRESALE_EVENTS = {
  // Step 1 — Create Account
  ADD_TO_CART: "Presale.AddToCart",
  ACCOUNT_REGISTRATION: "Presale.AccountRegistration",
  // Step 1b — Emailed code
  EMAIL_VERIFIED: "Presale.EmailVerified",
  // Step 2 — Add Place
  ADDRESS_ENTERED: "Presale.AddressEntered",
  WAITLIST_JOINED: "Presale.WaitlistJoined",
  // Step 3 — Confirm & Checkout
  INITIATE_CHECKOUT: "Presale.InitiateCheckout",
  PAYMENT_METHOD_ADDED: "Presale.PaymentMethodAdded",
  PAYMENT_METHOD_ADD_FAILED: "Presale.PaymentMethodAddFailed",
  PAYMENT_METHOD_CHANGED: "Presale.PaymentMethodChanged",
  PURCHASE: "Presale.Purchase",
  // Step 3 → 4 — the confirmed reservation
  SUBSCRIBE: "Presale.Subscribe",
  // Funnel-wide
  SIGN_OUT: "Presale.SignOut",
} as const;

export const LIVE_EVENTS = {
  START_CONNECT: "Live.StartConnect",
  CONNECT_SUCCEEDED: "Live.ConnectSucceeded",
} as const;

export const AUTH_EVENTS = {
  SIGN_OUT: "Auth.SignOut",
  SESSION_EXPIRED: "Auth.SessionExpired",
  USER_DEACTIVATED: "Auth.UserDeactivated",
};
